import { type ReactElement, useContext, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import WestIcon from "@mui/icons-material/West"
import { Grid } from "@mui/material"
import { Button, NotificationUtils, Snackbar, Tab, Tabs, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import { KonanEmptyState } from "../../../../../components/utility/KonanEmptyState"
import type { MapValueTypeToFeatureType } from "../../../../../types/custom/rules"
import type { WorkflowInputFeature } from "../../../../../types/generated/api/WorkflowInputFeature"
import { SMALL_ICON_SIZE } from "../../../../Predictions/Constants"
import { WorkflowSchemaContext } from "../../../context/Context"
import type { ConflictCardProps, ConflictsListProps, SchemaFeature } from "../../../types/interfaces"

import styles from "./ConflictsList.module.scss"

/**
 * ConflictCard component that displays individual feature conflicts in the schema dialog.
 * Shows original and new feature data with options to overwrite or ignore conflicts.
 */
const ConflictCard = (props: Readonly<ConflictCardProps>): ReactElement => {
  // Prop destructuring
  const { isUsed, oldFeatureData, NewFeatureData, handleOverwrite, handleIgnore } = props

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog.conflictsList",
  })

  return (
    <Grid item container className={styles.conflictCard}>
      <Grid item className={styles.conflictCardGrid} data-isUsed={isUsed}>
        <Tooltip title={oldFeatureData.name} width={"100%"} placement="top-start">
          <Typography variant="p" variantColor={2} noWrap style={{ textOverflow: "ellipsis", width: "100%" }}>
            {oldFeatureData.name}
          </Typography>
        </Tooltip>

        <Typography variant="p" variantColor={2}>
          {oldFeatureData.type}
        </Typography>

        <Grid item container>
          <Tag size="xsmall" color="blue">
            {t("tags.original")}
          </Tag>
        </Grid>

        <Tooltip title={NewFeatureData.name} width={"100%"} placement="top-start">
          <Typography variant="p" variantColor={2} noWrap style={{ textOverflow: "ellipsis", width: "100%" }}>
            {NewFeatureData.name}
          </Typography>
        </Tooltip>

        <Typography variant="p" variantColor={2}>
          {NewFeatureData.type}
        </Typography>

        <Grid item container>
          <Tag size="xsmall" color="green">
            {t("tags.incoming")}
          </Tag>
        </Grid>
      </Grid>

      {!isUsed && (
        <Grid item className={styles.conflictCardActions}>
          <Button fullWidth size="small" variant="secondary" onClick={handleIgnore}>
            {t("actions.ignore")}
          </Button>

          <Button fullWidth size="small" variant="dangerous" onClick={handleOverwrite}>
            {t("actions.overwrite")}
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

/**
 * ConflictsList component displays and manages a list of schema feature conflicts during workflow schema updates.
 *
 * - Shows conflicting features (unused and in-use) between the current and incoming schema.
 * - Allows users to resolve unused feature conflicts by either overwriting or ignoring them.
 * - In-use features cannot be overwritten and are shown as informational only.
 * - Provides bulk actions (Ignore All, Overwrite All) for unused features.
 * - Notifies users when all conflicts are resolved and updates the parent form state.

 * @param {FormikProps<WorkflowSchema>} formik - The Formik form state and helpers.
 * @param {{ [key: string]: SchemaFeature }} features - The current features object, keyed by feature name.
 * @returns ReactElement
 */
/**
 * ConflictsList component that manages feature conflicts in workflow schema configuration.
 * Displays conflicts between original and new features with resolution options.
 */
export function ConflictsList(props: Readonly<ConflictsListProps>): ReactElement {
  // Prop destructuring
  const { formik, features } = props

  // Context hooks
  const { setStep, conflictsList, setConflictsList } = useContext(WorkflowSchemaContext)

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog.conflictsList",
  })

  // State hooks
  const [activeTab, setActiveTab] = useState<"unUsed" | "inUse">("unUsed")
  // Local copy of the conflicts list for optimistic UI updates
  const [conflictsListCopy, setConflictsListCopy] = useState(conflictsList)

  // Refs
  // Ref to the features object, used to update feature types on overwrite
  const featuresObject = useRef<{
    [key: string]: SchemaFeature
  }>(features)

  // Helper functions
  /**
   * Handles resolving a single conflict by either overwriting the feature type or ignoring the conflict.
   *
   * @param conflictItem - Tuple of [featureName, { oldType, newType }]
   * @param overwrite - If true, overwrite the feature type; if false, ignore the conflict
   */
  const handleOverwrite = (
    conflictItem: [
      string,
      {
        oldType?: WorkflowInputFeature.type | MapValueTypeToFeatureType | undefined
        newType: WorkflowInputFeature.type
      },
    ],
    overwrite: boolean,
  ): void => {
    const featureName = conflictItem[0]
    const newType = conflictItem[1].newType

    let feature = featuresObject.current[featureName]

    if (feature && overwrite) {
      // If the feature exists and overwrite is true, update its type
      featuresObject.current[featureName] = {
        ...feature,
        type: newType,
      }
    }

    // Remove the handled conflict from the local copy
    const newCopy = { ...conflictsListCopy }
    delete newCopy.unUsedFeatures[featureName]

    // If all unused features are resolved, update the main conflicts list and form state
    if (Object.keys(newCopy.unUsedFeatures).length <= 0) {
      setConflictsList(newCopy)
      formik.setFieldValue("features", Object.values(featuresObject.current))

      // If all conflicts (unused and in-use) are resolved, show a notification
      if (Object.keys(newCopy.inUseFeatures).length <= 0) {
        NotificationUtils.toast(t("notifications.schemaModified.description"), {
          title: t("notifications.schemaModified.title"),
          severity: "success",
        })
      }

      // Go back to the previous step
      setStep(0)
    } else {
      // Otherwise, just update the local copy
      setConflictsListCopy(newCopy)
    }
  }

  return (
    <Grid item xs className={styles.root}>
      {/* Header with back button, title, and description */}
      <Grid item container className={styles.rootHeader}>
        <Grid item container>
          <Grid item container gap={1} alignItems={"center"}>
            {/* Back button: restores state and goes to previous step */}
            <Button
              size="small"
              variant="secondary"
              onClick={() => {
                if (Object.keys(conflictsList.unUsedFeatures).length > 0) {
                  setConflictsList(conflictsListCopy)
                  formik.setFieldValue("features", Object.values(featuresObject.current))
                }
                setStep(0)
              }}
            >
              <WestIcon style={{ color: "var(--secondary-icon-default)", ...SMALL_ICON_SIZE }} />
            </Button>
            <Typography variant="label-bold">
              {/* Total number of conflicts */}
              {t("header.title", {
                count:
                  Object.keys(conflictsListCopy.unUsedFeatures).length +
                  Object.keys(conflictsListCopy.inUseFeatures).length,
              })}
            </Typography>
          </Grid>

          <Typography variant="label-regular" variantColor={2}>
            {t("header.description")}
          </Typography>
        </Grid>

        {/* Bulk actions for unused features */}

        <Grid item container>
          <Button
            size="small"
            variant="secondary"
            disabled={Object.keys(conflictsListCopy.unUsedFeatures).length <= 0}
            onClick={async () => {
              // Ignore all unused feature conflicts
              await Object.entries(conflictsListCopy.unUsedFeatures).forEach((conflict) =>
                handleOverwrite(conflict, false),
              )
            }}
          >
            {t("actions.ignoreAll")}
          </Button>

          <Button
            size="small"
            variant="dangerous"
            onClick={async () => {
              // Overwrite all unused feature conflicts
              await Object.entries(conflictsListCopy.unUsedFeatures).forEach((conflict) =>
                handleOverwrite(conflict, true),
              )
            }}
            disabled={Object.keys(conflictsListCopy.unUsedFeatures).length <= 0}
          >
            {t("actions.overwriteAll")}
          </Button>
        </Grid>
      </Grid>

      {/* Tabs navigation for unused/in-use features */}
      <Grid item>
        <Tabs value={activeTab} className="base-tabs">
          <Tab
            key={"Unused Features"}
            label={t("tabs.unusedFeatures", { count: Object.keys(conflictsListCopy.unUsedFeatures).length })}
            onClick={() => setActiveTab("unUsed")}
            selected={activeTab === "unUsed"}
            size="large"
          />
          <Tab
            key={"In-use Features"}
            label={t("tabs.inUseFeatures", { count: Object.keys(conflictsListCopy.inUseFeatures).length })}
            onClick={() => setActiveTab("inUse")}
            selected={activeTab === "inUse"}
            size="large"
          />
        </Tabs>
      </Grid>

      {/* List of conflict cards or empty state */}
      <Grid item className={styles.conflictCardsContainer}>
        {/* Info snackbar for in-use features */}
        {activeTab === "inUse" && Object.keys(conflictsListCopy.inUseFeatures).length > 0 && (
          <Snackbar fullWidth severity="info" description={t("inUseInfoSnackbar.description")} />
        )}

        {/* Empty state if no conflicts in the selected tab */}
        {((activeTab === "unUsed" && Object.keys(conflictsListCopy.unUsedFeatures).length <= 0) ||
          (activeTab === "inUse" && Object.keys(conflictsListCopy.inUseFeatures).length <= 0)) && (
          <Grid item xs alignContent={"center"}>
            <KonanEmptyState
              title={activeTab === "inUse" ? t("emptyState.noInUseConflicts") : t("emptyState.noUnusedConflicts")}
            />
          </Grid>
        )}

        {/* Render conflict cards for the selected tab */}
        {Object.entries(activeTab === "inUse" ? conflictsListCopy.inUseFeatures : conflictsListCopy.unUsedFeatures).map(
          (conflict) => (
            <ConflictCard
              key={`${conflict[0]}`}
              isUsed={activeTab === "inUse"}
              oldFeatureData={{
                name: conflict[0],
                type: conflict[1].oldType as WorkflowInputFeature.type,
              }}
              NewFeatureData={{
                name: conflict[0],
                type: conflict[1].newType,
              }}
              handleOverwrite={activeTab === "unUsed" ? () => handleOverwrite(conflict, true) : undefined}
              handleIgnore={activeTab === "unUsed" ? () => handleOverwrite(conflict, false) : undefined}
            />
          ),
        )}
      </Grid>
    </Grid>
  )
}
